import { forwardRef, useEffect, useRef } from 'react';
import type { JSX, MouseEvent, ReactNode } from 'react';
import type { ToggleGroupItemProps } from '@radix-ui/react-toggle-group';
import { styled, css } from 'styled-components';
import { Item as ToggleGroupItem } from '@radix-ui/react-toggle-group';
import { isNotNil } from '@wistia/type-guards';
import { useSegmentedControlValue } from './useSegmentedControlValue';
import { mergeRefs } from '../../helpers/mergeRefs';
import { useSelectedItemStyle } from './useSelectedItemStyle';
import { selectedItemIndicatorStyles } from './SelectedItemIndicator';

type StyledSegmentedControlItemProps = ToggleGroupItemProps & {
  $hasLabel?: boolean;
};

export const segmentedControlItemStyles = css<StyledSegmentedControlItemProps>`
  all: unset; /* ToggleGroupItem is a button element */
  align-items: center;
  border-radius: var(--wui-border-radius-rounded);
  color: var(--wui-color-text-secondary);
  cursor: pointer;
  display: flex;
  gap: 2px;
  flex-grow: 1;
  flex-shrink: 1;
  font-size: var(--wui-typography-label-3-size);
  font-weight: var(--wui-typography-label-3-weight);
  line-height: var(--wui-typography-label-3-line-height);
  justify-content: center;
  padding: 6px ${({ $hasLabel }) => ($hasLabel ? '12px' : '6px')};
  user-select: none;
  transition: background-color var(--wui-motion-duration-01) var(--wui-motion-ease);

  /* Icon component */
  svg {
    height: 16px;
    width: 16px;
    flex: 0 0 16px;

    /* add right margin if there's a label and an icon */
    ${({ $hasLabel }) => ($hasLabel ? 'margin-right: var(--wui-space-01)' : '')}
  }

  &:focus-visible {
    outline: 2px solid var(--wui-color-focus-ring);
  }

  /* note: react-toggle-group uses 'aria-selected' and react-tabs uses "active" */

  /* we can't style off of 'data-state' since that gets overridden when wrapped in a Tooltip */
  &:hover:not([aria-checked='true'], [aria-selected='true']) {
    background-color: var(--wui-color-bg-surface-secondary-hover);
  }

  &[aria-checked='true'],
  &[aria-selected='true'] {
    color: var(--wui-color-segmented-color-text-override, var(--wui-color-text-selected));
    cursor: default;
    transition: none;

    svg path {
      fill: var(--wui-color-icon-selected);
    }

    /* This mkaes sure that the active styles appear on the initial value when the component renders */
    &:not(:has(~ [data-wui-selected-item-indicator])) {
      ${selectedItemIndicatorStyles}
    }
  }

  &[data-disabled] {
    cursor: not-allowed;
    color: var(--wui-color-text-disabled);

    svg path {
      fill: var(--wui-color-icon-disabled);
    }

    &:hover {
      background-color: inherit;
    }
  }
`;

const StyledSegmentedControlItem = styled(ToggleGroupItem)<StyledSegmentedControlItemProps>`
  ${segmentedControlItemStyles}
`;

type BaseProps = {
  /**
   * Disable the `SegmentedControlItem`
   */
  disabled?: boolean | undefined;
  /**
   * The value of the item
   */
  value: string;
};

type IconWithLabel = {
  /**
   * The [Icon](?path=/docs/components-icon--docs) component to use, e.g. `<Icon type="favorite" />`
   */
  icon?: JSX.Element;
  /**
   * The label of the item
   */
  label?: ReactNode | string | undefined;
  'aria-label'?: never;
};

type IconWithAriaLabel = {
  /**
   * The [Icon](?path=/docs/components-icon--docs) component to use, e.g. `<Icon type="favorite" />`
   */
  icon?: JSX.Element;
  /**
   * Use this when displaying an icon without a label
   */
  'aria-label'?: string | undefined;
  label?: never;
};

type NoIcon = {
  icon?: undefined;
  /**
   * The label of the item
   */
  label?: ReactNode | string | undefined;
  'aria-label'?: never;
};

export type SegmentedControlItemProps = BaseProps & (IconWithAriaLabel | IconWithLabel | NoIcon);

export const SegmentedControlItem = forwardRef<HTMLButtonElement, SegmentedControlItemProps>(
  (
    { disabled, icon, label, 'aria-label': ariaLabel, value, ...otherProps },
    forwardedRef,
  ): JSX.Element => {
    const selectedValue = useSegmentedControlValue();
    const { setSelectedItemMeasurements } = useSelectedItemStyle();

    const buttonRef = useRef<HTMLButtonElement>(null);

    const combinedRef = mergeRefs([buttonRef, forwardedRef]);

    // note: this is necessary because ToggleGroupItem is toggleable and
    // in the event that the user clicks on the selected item, it will toggle off
    // so we prevent that from happening here
    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
      const target = event.target as HTMLElement;
      const { state } = target.dataset;

      if (state === 'on') {
        event.preventDefault();
      }
    };

    useEffect(() => {
      const buttonElem = buttonRef.current;

      if (!buttonElem) {
        return undefined;
      }

      if (selectedValue !== value) {
        return undefined;
      }

      setSelectedItemMeasurements({
        offsetLeft: buttonElem.offsetLeft,
        offsetWidth: buttonElem.offsetWidth,
        offsetHeight: buttonElem.offsetHeight,
      });

      const resizeObserver = new ResizeObserver(() => {
        setSelectedItemMeasurements({
          offsetLeft: buttonElem.offsetLeft,
          offsetWidth: buttonElem.offsetWidth,
          offsetHeight: buttonElem.offsetHeight,
        });
      });

      resizeObserver.observe(buttonElem);

      return () => {
        resizeObserver.disconnect();
      };
    }, [selectedValue, setSelectedItemMeasurements, value]);

    return (
      <StyledSegmentedControlItem
        ref={combinedRef}
        // We need to spread props here so that you can wrap SegmentedControlItem in a Tooltip
        // https://www.radix-ui.com/primitives/docs/guides/composition#your-component-must-spread-props
        {...otherProps}
        $hasLabel={isNotNil(label)}
        aria-label={isNotNil(label) ? undefined : ariaLabel}
        disabled={disabled}
        onClick={handleClick}
        value={value}
      >
        {icon}
        {label}
      </StyledSegmentedControlItem>
    );
  },
);

SegmentedControlItem.displayName = 'SegmentedControlItem';
